
import { defineComponent, computed, PropType } from "vue";
import { Radar as RadarChart } from "vue-chartjs";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import { GroupedAlert } from "@/stores/telegramAlerts";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export default defineComponent({
  name: "RiskRewardRadar",
  components: {
    RadarChart,
  },
  props: {
    alerts: {
      type: Array as PropType<GroupedAlert[]>,
      required: true,
    },
  },
  setup(props) {
    // Custom plugin to draw labels
    const customDataLabels = {
      id: "customDataLabels",
      afterDatasetsDraw(chart: any) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, i: number) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((element: any, index: number) => {
            const value = dataset.data[index];
            if (value !== 0) {
              const { x, y } = element.getProps(["x", "y"], true);
              ctx.save();
              ctx.fillStyle = "#333";
              ctx.font = "bold 11px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              // Add simple outline for readability
              ctx.shadowColor = "white";
              ctx.shadowBlur = 4;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              ctx.fillText(value, x, y - 10); // Draw slightly above
              ctx.restore();
            }
          });
        });
      },
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 0,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `Trades: ${context.parsed.r}`;
            },
          },
        },
      },
      scales: {
        r: {
          angleLines: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
          grid: {
            color: "rgba(0, 0, 0, 0.05)",
          },
          ticks: {
            display: false,
            backdropColor: "transparent",
          },
          pointLabels: {
            font: {
              size: 11,
            },
            padding: 10,
          },
          suggestedMin: 0,
        },
      },
    };

    const plugins = [customDataLabels];

    const hasData = computed(() => {
      return props.alerts.some((a) => {
        const msg = a.mainMessage;
        return (
          msg.entryPrice &&
          msg.stopLoss &&
          msg.takeProfit &&
          msg.takeProfit.length > 0
        );
      });
    });

    const chartData = computed((): ChartData<"radar", number[], string> => {
      // 1. Calculate and collect all RRR values
      const rrrs: number[] = [];

      props.alerts.forEach((alert) => {
        const msg = alert.mainMessage;
        if (
          msg.entryPrice !== undefined &&
          msg.stopLoss !== undefined &&
          msg.takeProfit &&
          msg.takeProfit.length > 0
        ) {
          const entry = msg.entryPrice;
          const sl = msg.stopLoss;
          // Use last TP for RRR calculation to match table logic (Max Reward)
          const tpLast = msg.takeProfit[msg.takeProfit.length - 1];

          const risk = Math.abs(entry - sl);
          const reward = Math.abs(tpLast - entry);

          if (risk > 0) {
            // Standard RRR Calculation (Reward / Risk)
            // e.g. 1:0.5 -> RRR 0.5
            // e.g. 1:22 -> RRR 22
            const rrr = reward / risk;
            rrrs.push(rrr);
          }
        }
      });

      // 2. Determine buckets based on data range
      const maxRR = rrrs.length > 0 ? Math.max(...rrrs) : 0;
      let buckets: { label: string; min: number; max: number }[] = [];

      // If we have high RRR trades (e.g. > 5), scale the buckets
      if (maxRR > 5) {
        // Calculate a nice step size
        // e.g. max 22 -> ceil(22)=22. 22/5 = 4.4 -> step 5. Buckets: 0-5, 5-10, 10-15, 15-20, 20-25.
        // e.g. max 8 -> 8/5 = 1.6 -> step 2. Buckets: 0-2, 2-4, 4-6, 6-8, 8-10.
        const step = Math.ceil(Math.ceil(maxRR) / 5);

        for (let i = 0; i < 5; i++) {
          const start = i * step;
          const end = (i + 1) * step;
          let label: string;

          // Adjust first bucket label: "1:0" is weird, use "< 1:step"
          if (i === 0) {
            label = `< 1:${end}`;
          } else if (i === 4) {
            // For the last bucket, show open-ended upper range
            label = `> 1:${start}`;
          } else {
            label = `1:${start} - 1:${end}`;
          }

          buckets.push({
            label: label,
            min: i === 0 ? -0.01 : start,
            max: i === 4 ? 999999 : end,
          });
        }
      } else {
        // Default small buckets for low RRR environments
        // Using exclusive lower bound and inclusive upper bound to avoid boundary overlap
        buckets = [
          { label: "< 1:1", min: -0.01, max: 1.0 },
          { label: "1:1 - 1:2", min: 1.0, max: 2.0 },
          { label: "1:2 - 1:3", min: 2.0, max: 3.0 },
          { label: "1:3 - 1:4", min: 3.0, max: 4.0 },
          { label: "> 1:4", min: 4.0, max: 999 },
        ];
      }

      // 3. Count frequencies
      const counts = new Array(buckets.length).fill(0);
      rrrs.forEach((rrr) => {
        // Find bucket using exclusive lower bound and inclusive upper bound
        const index = buckets.findIndex((b) => rrr > b.min && rrr <= b.max);
        if (index !== -1) {
          counts[index]++;
        }
      });

      return {
        labels: buckets.map((b) => b.label),
        datasets: [
          {
            label: "Trades",
            data: counts,
            backgroundColor: "rgba(231, 74, 59, 0.2)", // Danger Red transparent
            borderColor: "rgba(231, 74, 59, 1)", // Danger Red solid
            borderWidth: 2,
            pointBackgroundColor: "rgba(231, 74, 59, 1)",
          },
        ],
      };
    });

    return {
      chartData,
      chartOptions,
      plugins,
      hasData,
    };
  },
});
